import { ID, WorkspaceConfirmationModalName } from "@nova/constants"
import { createAppAsyncThunk, createAppThunk } from "integrations/redux/hooks"
import { selectActiveWorkspaceId, updateTag, deleteTag } from "../WorkspaceManagementSlice"
import { updateFire } from "features/entity-caching/fires/thunks"
import { showConfirmationModal } from "features/content-overlay/thunks"
import { showGlobalModal } from "runlevel/actions"
import {
    setInviteModalId,
    updateWorkspaceMember,
} from "features/entity-caching/members/UserManagementSlice"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { selectUserID } from "user/profile/selectors"
import { WorkspaceEntityType } from "../types"
import { findTagAncestorIds } from "../module"
import {
    selectAllSubEntitiesByFolderId,
    selectAllFoldersById,
    selectFavoritesData,
} from "./selectors"
import { get } from "nodash"

export const updateRecentDate = createAppThunk((id: string, { getState, dispatch }) => {
    const state = getState()
    const activeWorkspaceId = selectActiveWorkspaceId()(state)
    const userId = selectUserID(state)
    if (!activeWorkspaceId || !userId) return
    dispatch(
        updateWorkspaceMember({
            workspaceId: activeWorkspaceId,
            userId,
            attributes: { recentId: id },
        })
    )
})

export const toggleFavorite = createAppAsyncThunk(
    "workspace/toggleFavorite",
    async (id: ID, { getState, dispatch }) => {
        const state = getState()
        const activeWorkspaceId = selectActiveWorkspaceId()(state)
        const userId = selectUserID(state)
        if (!activeWorkspaceId || !userId) return []

        const { favoriteIds } = selectFavoritesData(state)
        const mutableFavorites = new Set(favoriteIds)
        if (mutableFavorites.has(id)) mutableFavorites.delete(id)
        else mutableFavorites.add(id)

        const result = await dispatch(
            updateWorkspaceMember({
                workspaceId: activeWorkspaceId,
                userId,
                attributes: { favorites: Array.from(mutableFavorites) },
            })
        )
        return get(result.payload, "favorites", []) as ID[]
    }
)

export const overwriteFavorites = createAppThunk(
    (favoriteIds: string[], { getState, dispatch }) => {
        const state = getState()
        const activeWorkspaceId = selectActiveWorkspaceId()(state)
        const userId = selectUserID(state)
        if (!activeWorkspaceId || !userId) return
        dispatch(
            updateWorkspaceMember({
                workspaceId: activeWorkspaceId,
                userId,
                attributes: { favorites: favoriteIds },
            })
        )
    }
)

export const moveWorkspaceEntity = createAppThunk(
    (
        {
            id,
            type,
            newParentFolderId,
        }: { id: string; type: WorkspaceEntityType; newParentFolderId?: string },
        { getState, dispatch }
    ) => {
        const state = getState()
        const activeWorkspaceId = selectActiveWorkspaceId()(state)
        if (type === WorkspaceEntityType.Folder) {
            const allFoldersById = selectAllFoldersById(state)
            const folder = allFoldersById[id]
            if (!activeWorkspaceId || !folder) return
            dispatch(
                updateTag({
                    id,
                    updates: { parentId: newParentFolderId || "root" },
                })
            )
        } else {
            dispatch(updateFire({ id, updates: { tagId: newParentFolderId || "root" } }))
        }
    }
)

export const deleteWorkspaceEntity = createAppAsyncThunk(
    "workspace/deleteEntity",
    async (
        { id, name, type }: { id: string; name: string; type: WorkspaceEntityType },
        { getState, dispatch }
    ) => {
        const state = getState()
        const activeWorkspaceId = selectActiveWorkspaceId()(state)
        const isFolder = type === WorkspaceEntityType.Folder

        if (isFolder) {
            const { subChains, projectIds } = selectAllSubEntitiesByFolderId(id)(state)
            if (
                !projectIds.length &&
                subChains.length === 1 &&
                subChains[0].subTags.length === 1 &&
                subChains[0].id === id
            ) {
                await dispatch(deleteTag({ id, activeWorkspaceId: activeWorkspaceId || "" }))
                return
            }

            const activeWorkspace = state["workspace-management-v1"].activeWorkspace
            const filteredTagIds = activeWorkspace
                ? findTagAncestorIds(activeWorkspace.tags, id)
                : [id]

            dispatch(
                showConfirmationModal({
                    type: WorkspaceConfirmationModalName.DeleteFolder,
                    entityId: id,
                    entityName: name,
                    filteredTagIds,
                })
            )
        } else {
            dispatch(
                showConfirmationModal({
                    type: WorkspaceConfirmationModalName.DeleteProject,
                    entityId: id,
                    entityName: name,
                })
            )
        }

        dispatch(showGlobalModal())
    }
)

export const openWorkspaceShareModal = createAppAsyncThunk(
    "workspace/openShareModal",
    (id: ID | undefined, { dispatch }) => {
        dispatch(setInviteModalId(id))
        dispatch(initializeCenterModalForComponentAction(ModalNames.InviteEverywhereModal))
        dispatch(showGlobalModal())
    }
)
